import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import classNames from 'classnames';
import CtaButton from '../ctaButton/ctaButton';
import GradientLines from '../gradientLines/gradientLines';
import Reveal from '../reveal/reveal';
import handleCTAClick from '../../services/helpers/handleCTAClick';
import styles from './ctaBanner.module.scss';

function CtaBanner({
  headline = '',
  subHeadline = '',
  description = '',
  offerText = '',
  cta = null,
  secondaryCta = null,
  theme = 'darkBlue',
  showGradientLines = false,
  isMediaCenter = true,
  enableAnimations = false,
  config = {
    containerSize: null,
    headLine: {
      position: 'center',
    },
  },
}) {
  const isEggshell = theme === 'eggshell';
  // Entrance reveal is scoped to the eggshell theme and gated by the CMS flag.
  const animate = isEggshell && enableAnimations;

  const headlineClass = config.headLine.position
    ? `${styles.ctaBannerTitle} ${styles[config.headLine.position]}`
    : styles.ctaBannerTitle;
  const containerClass = classNames(styles.ctaBanner, {
    [styles[config.containerSize]]: config.containerSize,
    [styles.eggshellWrapper]: isEggshell,
  });

  const contentClass = classNames(styles.ctaBannerContent, {
    [styles.darkTheme]:
      theme !== 'white' &&
      theme !== 'grey' &&
      theme !== 'homeHeading' &&
      theme !== 'eggshell' &&
      theme !== 'orange',
    [styles.whiteTheme]:
      theme === 'white' || theme === 'grey' || theme === 'homeHeading',
    [styles.homeHeadingTheme]: theme === 'homeHeading',
    [styles.eggshellTheme]: isEggshell,
    [styles.orangeTheme]: theme === 'orange',
  });

  return (
    <div className={containerClass} data-theme={isEggshell ? 'eggshell' : null}>
      {isEggshell && showGradientLines && (
        <GradientLines animate={enableAnimations} />
      )}
      <div className={contentClass}>
        {offerText && (
          <Reveal
            enabled={animate}
            variant="up"
            className={styles.ctaBannerOfferText}
          >
            {offerText}
          </Reveal>
        )}
        {headline && isMediaCenter ? (
          <Reveal
            enabled={animate}
            as="h1"
            variant="up"
            delay={80}
            className={headlineClass}
          >
            {parse(headline)}
          </Reveal>
        ) : (
          headline && (
            <Reveal
              enabled={animate}
              as="h2"
              variant="up"
              delay={80}
              className={headlineClass}
            >
              {parse(headline)}
            </Reveal>
          )
        )}
        {subHeadline && (
          <Reveal
            enabled={animate}
            as="p"
            variant="up"
            delay={160}
            className={styles.ctaBannerSubHeadline}
          >
            {parse(subHeadline)}
          </Reveal>
        )}
        {description && (
          <Reveal
            enabled={animate}
            as="p"
            variant="up"
            delay={200}
            className={styles.ctaBannerText}
          >
            {parse(description)}
          </Reveal>
        )}
        {(cta || secondaryCta) && (
          <Reveal
            enabled={animate}
            variant="up"
            delay={280}
            className={styles.getStartedBtn}
          >
            {cta && (
              <CtaButton
                label={cta.label}
                id="apply_online_cta"
                data-testid="apply_online_cta"
                className={styles.getStartedCta}
                variant={cta.variant}
                color={cta.color}
                type="button"
                onClick={(e) => handleCTAClick(e, cta)}
                href={cta?.link}
              />
            )}
            {secondaryCta && (
              <CtaButton
                label={secondaryCta.label}
                className={styles.secondaryCta}
                variant={secondaryCta.variant}
                color={secondaryCta.color}
                type="button"
                onClick={(e) => handleCTAClick(e, secondaryCta)}
                href={secondaryCta?.link}
              />
            )}
          </Reveal>
        )}
      </div>
    </div>
  );
}
export default CtaBanner;

CtaBanner.propTypes = {
  headline: PropTypes.string,
  subHeadline: PropTypes.string,
  description: PropTypes.string,
  offerText: PropTypes.string,
  cta: PropTypes.shape({
    label: PropTypes.string,
    link: PropTypes.string,
    variant: PropTypes.string,
    color: PropTypes.string,
  }),
  secondaryCta: PropTypes.shape({
    label: PropTypes.string,
    link: PropTypes.string,
    variant: PropTypes.string,
    color: PropTypes.string,
  }),
  theme: PropTypes.string,
  showGradientLines: PropTypes.bool,
  isMediaCenter: PropTypes.bool,
  enableAnimations: PropTypes.bool,
  config: PropTypes.shape({
    containerSize: PropTypes.oneOf([null, 'medium', 'small']),
    headLine: PropTypes.shape({
      position: PropTypes.oneOf([null, 'left', 'center', 'right']),
    }),
  }),
};
